export interface ProjectionResults {
  // Primary metrics
  monthlyRevenue: number;
  annualRunRate: number;
  monthlyNetProfit: number;
  profitMargin: number;

  // Per-pod metrics
  revenuePerPod: number;
  profitPerPod: number;
  paybackPeriod: number;

  // Cost breakdown
  totalRevenue: number;
  revenueShareToHosts: number;
  totalOperatingCosts: number;
  fixedOverhead: number;
  
  // Additional details
  utilizedHoursPerPod: number;
  costPerPod: number;
  breakEvenPods: number;
}

export interface AssumptionInputs {
  hoursPerMonth: number;
  revenueSharePercent: number;
  maintenanceCostPerPod: number;
  insuranceCostPerPod: number;
  paymentProcessingPercent: number;
  fixedOverhead: number;
  initialPodCost: number;
}

export const calculateProjections = (
  numberOfPods: number,
  pricePerHour: number,
  utilizationPercent: number,
  assumptions: AssumptionInputs
): ProjectionResults => {
  // Basic calculations
  const utilizedHoursPerPod = assumptions.hoursPerMonth * (utilizationPercent / 100);
  const revenuePerPod = utilizedHoursPerPod * pricePerHour;
  const totalRevenue = revenuePerPod * numberOfPods;

  // Costs per pod
  const revenueSharePerPod = revenuePerPod * (assumptions.revenueSharePercent / 100);
  const paymentProcessingPerPod = revenuePerPod * (assumptions.paymentProcessingPercent / 100);
  const costPerPod = revenueSharePerPod + assumptions.maintenanceCostPerPod + assumptions.insuranceCostPerPod + paymentProcessingPerPod;

  // Total costs
  const revenueShareToHosts = revenueSharePerPod * numberOfPods;
  const totalOperatingCosts = costPerPod * numberOfPods;
  const totalCosts = totalOperatingCosts + assumptions.fixedOverhead;

  // Profit calculations
  const monthlyNetProfit = totalRevenue - totalCosts;
  const annualRunRate = monthlyNetProfit * 12;
  const profitMargin = totalRevenue > 0 ? (monthlyNetProfit / totalRevenue) * 100 : 0;
  
  const profitPerPod = revenuePerPod - costPerPod - (assumptions.fixedOverhead / numberOfPods);
  const paybackPeriod = profitPerPod > 0 ? assumptions.initialPodCost / profitPerPod : 0;

  // Break-even calculation
  const contributionPerPod = revenuePerPod - costPerPod;
  const breakEvenPods = contributionPerPod > 0 ? Math.ceil(assumptions.fixedOverhead / contributionPerPod) : 0;

  return {
    monthlyRevenue: totalRevenue,
    annualRunRate,
    monthlyNetProfit,
    profitMargin,
    revenuePerPod,
    profitPerPod,
    paybackPeriod,
    totalRevenue,
    revenueShareToHosts,
    totalOperatingCosts,
    fixedOverhead: assumptions.fixedOverhead,
    utilizedHoursPerPod,
    costPerPod,
    breakEvenPods,
  };
};

export const generateMonthlyProjections = (
  numberOfPods: number,
  pricePerHour: number,
  utilizationPercent: number,
  assumptions: AssumptionInputs
) => {
  const months = [];
  
  for (let month = 1; month <= 12; month++) {
    // Ramp up utilization: 60% of target in month 1, 80% in month 2, 100% from month 3
    let actualUtilization = utilizationPercent;
    if (month === 1) {
      actualUtilization = utilizationPercent * 0.6;
    } else if (month === 2) {
      actualUtilization = utilizationPercent * 0.8;
    }

    const projections = calculateProjections(numberOfPods, pricePerHour, actualUtilization, assumptions);
    
    months.push({
      month,
      monthName: new Date(2024, month - 1).toLocaleString('default', { month: 'short' }),
      revenue: projections.monthlyRevenue,
      profit: projections.monthlyNetProfit,
      utilization: actualUtilization,
    });
  }

  return months;
};
